import {keepPreviousData} from "@tanstack/react-query";
import type {GridFilterModel, GridRowModel, GridSortModel} from "@mui/x-data-grid-premium";
import {BaseMuixApi} from "~/components/muix-data-grid/BaseMuixApi";
import {EMPTY_FILTER} from "~/components/muix-data-grid/MuixDataGridConstants";
import {flattenRelatedDataFields} from "~/services/ApiHelpers";
import type {NestedRelatedDataModel} from "~/services/ApiHelpers";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {supplierTransactionsGridUrl} from "~/pages/supplier-360/services/supplier360Urls";

/**
 * The related rows the columns render. Each key is also what makes its fields filterable and
 * sortable: the endpoint resolves a flat field name to its ORM path through this map
 * (`p_ddate_1` → `part__p_ddate_1`, `l1` → `cat__l1`) — docs §2.21.
 */
const RELATED_DATA: NestedRelatedDataModel = {
    part: ['p_name', 'p_ddate_1', 'p_description'],
    cat: ['l1'],
};

/** `{"rows": [...], "rowCount": N}` — the shape every `BaseMuixViewSet.grid()` returns. */
type SupplierTransactionsGridResponse = {
    rows: GridRowModel[];
    rowCount: number;
};

export type SupplierTransactionsQueryVariables = {
    entityId: number;
    /** `undefined` while the databag is still resolving — the query stays disabled until it isn't. */
    databagId: number | undefined;
    /** 1-based, as the endpoint wants it. */
    page: number;
    pageSize: number;
    sortModel: GridSortModel;
    filterModel: GridFilterModel;
};

/**
 * One page of a supplier's transactions — `GET supplier_360/transaction/muix/grid/` (docs §2.21).
 *
 * A MUIX grid endpoint rather than a plain list, so the query params are built by the shared
 * `BaseMuixApi.getMuiXQuery()` (static, no controller needed) plus this endpoint's own `entity`.
 * Sorting, filtering and paging are all resolved server-side; the grid state they come from is the
 * module's usual `useDataGridQueryState`, and every change is simply a new query key.
 *
 * `related_part` / `related_cat` are flattened onto each row (`p_ddate_1`, `p_description`, `l1`),
 * so a column reads the field directly — the same expansion the MUIX table controllers do.
 */
export function useSupplierTransactionsQuery({
                                                 entityId,
                                                 databagId,
                                                 page,
                                                 pageSize,
                                                 sortModel,
                                                 filterModel,
                                             }: SupplierTransactionsQueryVariables) {
    const url = supplierTransactionsGridUrl();
    const params = databagId === undefined ? undefined : BaseMuixApi.getMuiXQuery(databagId, {
        sortModel,
        // The grid's own empty model carries only `items`, while the endpoint requires the full
        // MUIX shape (`logicOperator`, `quickFilterValues`, `quickFilterLogicOperator`) — it
        // answers `400 {"filterModel": {"quickFilterValues": ["This field is required."]}}` without them.
        filterModel: {...EMPTY_FILTER, ...filterModel},
        relatedData: RELATED_DATA,
        page,
        pageSize,
    });
    params?.set('entity', String(entityId));

    return useCustomQuery({
        // The serialized params are the key: a new page, sort or filter is a different query.
        queryKey: [url, {params: params?.toString()}] as const,
        queryFn: async ({signal}) => {
            const {data} = await queryHttp.get<SupplierTransactionsGridResponse>(url, {params, signal});
            return {
                rowCount: data.rowCount,
                rows: data.rows.map(row => {
                    flattenRelatedDataFields(row, RELATED_DATA);
                    return row;
                }),
            };
        },
        enabled: Boolean(entityId) && databagId !== undefined,
        // Paging and filtering keep the current page on screen instead of emptying the grid.
        placeholderData: keepPreviousData,
        showErrorNotification: true,
    });
}
